import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MagnifyingGlass, X, CaretDown, User, Clock, Brain,
  CalendarBlank, CheckCircle, Warning, XCircle,
  ArrowCounterClockwise, Spinner, ClipboardText, Plus,
  BookBookmark,
} from "@phosphor-icons/react";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { api } from "../api";

// ── Status badge config ──────────────────────────────────────────────────────

const STATUS_CONFIG = {
  trialing:       { label: "Trialing",       variant: "warning",   icon: Clock },
  active:         { label: "Active",          variant: "success",   icon: CheckCircle },
  cancelled:      { label: "Cancelled",       variant: "default",   icon: XCircle },
  expired:        { label: "Expired",         variant: "destructive", icon: XCircle },
  payment_failed: { label: "Payment failed",  variant: "destructive", icon: Warning },
};

const WORD_STATUS_VARIANT = {
  approved: "success",
  review:   "warning",
  draft:    "neutral",
  rejected: "destructive",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status ?? "—", variant: "default", icon: User };
  const Icon = cfg.icon;
  return (
    <BloomBadge variant={cfg.variant} className="flex items-center gap-1 px-2 py-0.5 text-xs">
      <Icon size={11} weight="bold" />
      {cfg.label}
    </BloomBadge>
  );
}

function RoleBadge({ role }) {
  return (
    <BloomBadge variant={role === "admin" ? "primary" : "neutral"} className="px-2 py-0.5 text-xs capitalize">
      {role}
    </BloomBadge>
  );
}

function PlanBadge({ plan }) {
  if (!plan) return null;
  return (
    <BloomBadge variant="teal" className="px-2 py-0.5 text-xs capitalize">
      {plan}
    </BloomBadge>
  );
}

// ── Last-seen formatter ──────────────────────────────────────────────────────

function formatRelative(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2)   return "Just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ── Extend Trial drawer ──────────────────────────────────────────────────────

function ExtendTrialPanel({ user, onDone, onClose }) {
  const [days, setDays]   = useState(7);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  async function submit() {
    setSaving(true);
    setMsg(null);
    try {
      const res = await api(`/admin/users/${user.user_id}/extend-trial`, {
        method: "PATCH",
        body: { days },
      });
      setMsg({ ok: true, text: `Trial extended to ${formatDate(res.new_trial_end)}` });
      setTimeout(() => { onDone(); onClose(); }, 1200);
    } catch (err) {
      setMsg({ ok: false, text: err?.message ?? "Failed to extend trial." });
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-muted/50 p-4">
      <p className="font-display text-sm font-black text-foreground">Extend free trial</p>
      <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
        Adds days to their trial. If already expired, extension starts from today.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-1.5">
          <button
            onClick={() => setDays(d => Math.max(1, d - 1))}
            className="text-lg font-black text-muted-foreground hover:text-foreground"
          >−</button>
          <span className="w-8 text-center font-display text-lg font-black text-foreground">{days}</span>
          <button
            onClick={() => setDays(d => Math.min(365, d + 1))}
            className="text-lg font-black text-muted-foreground hover:text-foreground"
          >+</button>
        </div>
        <span className="text-sm font-semibold text-muted-foreground">days</span>
        <button
          onClick={submit}
          disabled={saving}
          className="ml-auto flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-extrabold text-white disabled:opacity-60"
        >
          {saving ? <Spinner size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
          Extend
        </button>
      </div>
      {msg && (
        <p className={`mt-2 text-xs font-bold ${msg.ok ? "text-success-ink" : "text-destructive"}`}>
          {msg.text}
        </p>
      )}
    </div>
  );
}

// ── Word requests section ────────────────────────────────────────────────────

function WordRequestsSection({ userId }) {
  const [words, setWords]   = useState(null);
  const [error, setError]   = useState(false);

  useEffect(() => {
    api(`/admin/users/${userId}/word-requests`)
      .then(setWords)
      .catch(() => { setError(true); setWords([]); });
  }, [userId]);

  if (words === null) {
    return (
      <div className="space-y-2">
        {[1, 2].map(i => (
          <div key={i} className="h-10 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  if (error || words.length === 0) {
    return (
      <p className="rounded-2xl bg-muted/50 py-4 text-center text-xs font-semibold text-muted-foreground">
        {error ? "Couldn't load word requests." : "No word requests yet."}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      {words.map((w) => (
        <div
          key={w.word_id ?? w.word}
          className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2"
        >
          <span className="min-w-0 flex-1 font-display text-sm font-extrabold capitalize text-foreground truncate">
            {w.word}
          </span>
          <span className="shrink-0 text-[10px] font-semibold text-muted-foreground">
            {formatDate(w.created_at)}
          </span>
          <BloomBadge
            variant={WORD_STATUS_VARIANT[w.status] ?? "neutral"}
            className="shrink-0 px-2 py-0.5 text-[10px] capitalize"
          >
            {w.status}
          </BloomBadge>
        </div>
      ))}
    </div>
  );
}

// ── User detail drawer ───────────────────────────────────────────────────────

function UserDrawer({ user, onClose, onRefresh }) {
  const [showExtend, setShowExtend] = useState(false);
  if (!user) return null;

  const daysLeft = user.days_left;
  const hasWordRequests = (user.word_requests_count ?? 0) > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 26, stiffness: 220 }}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col bg-background shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <p className="font-display text-lg font-black text-foreground truncate">{user.name || user.email}</p>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* Identity */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Account</p>
            <p className="text-sm font-semibold text-foreground break-all">{user.email}</p>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              <RoleBadge role={user.role} />
              <StatusBadge status={user.subscription_status} />
              {user.plan && <PlanBadge plan={user.plan} />}
            </div>
          </div>

          {/* Stats grid — 2×2 */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Children",   value: user.children_count ?? 0,       icon: User },
              { label: "Quizzes",    value: user.total_quizzes ?? 0,         icon: Brain },
              { label: "Plans",      value: user.plans_count ?? 0,           icon: ClipboardText },
              { label: "Words",      value: user.word_requests_count ?? 0,   icon: BookBookmark },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl bg-muted/50 p-3 text-center">
                <Icon size={16} weight="bold" className="mx-auto text-muted-foreground" />
                <p className="mt-1 font-display text-xl font-black text-foreground">{value}</p>
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>

          {/* Dates */}
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Dates</p>
            {[
              { label: "Joined",       value: formatDate(user.created_at) },
              { label: "Last login",   value: formatRelative(user.last_login_at) },
              { label: "Trial ends",   value: user.trial_end ? formatDate(user.trial_end) : "—",
                extra: user.subscription_status === "trialing" && daysLeft !== null
                  ? ` (${daysLeft}d left)` : null },
              { label: "Plan renews",  value: formatDate(user.stripe_subscription_period_end) },
            ].map(({ label, value, extra }) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="font-semibold text-muted-foreground">{label}</span>
                <span className="font-bold text-foreground">
                  {value}{extra && <span className="text-muted-foreground">{extra}</span>}
                </span>
              </div>
            ))}
          </div>

          {/* Word requests list */}
          {hasWordRequests && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <BookBookmark size={13} weight="bold" className="text-muted-foreground" />
                <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  Word requests ({user.word_requests_count})
                </p>
              </div>
              <WordRequestsSection userId={user.user_id} />
            </div>
          )}

          {/* Extend trial */}
          {user.role !== "admin" && (
            <div>
              <button
                onClick={() => setShowExtend(s => !s)}
                className="flex w-full items-center gap-2 rounded-2xl border border-border px-4 py-2.5 text-sm font-extrabold text-foreground hover:bg-muted"
              >
                <ArrowCounterClockwise size={15} weight="bold" />
                Extend free trial
                <CaretDown size={13} className={`ml-auto transition-transform ${showExtend ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showExtend && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    style={{ overflow: "hidden" }}
                    className="mt-2"
                  >
                    <ExtendTrialPanel
                      user={user}
                      onDone={onRefresh}
                      onClose={onClose}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

// ── Status filter pill ───────────────────────────────────────────────────────

const FILTERS = [
  { value: "",               label: "All" },
  { value: "trialing",       label: "Trialing" },
  { value: "active",         label: "Active" },
  { value: "cancelled",      label: "Cancelled" },
  { value: "expired",        label: "Expired" },
  { value: "payment_failed", label: "Failed" },
];

// ── User row ─────────────────────────────────────────────────────────────────

function UserRow({ user, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition hover:bg-muted/50"
    >
      {/* Avatar initial */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/12 font-display text-sm font-black text-primary-strong">
        {(user.name || user.email)?.[0]?.toUpperCase() ?? "?"}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-display text-sm font-black text-foreground truncate">
            {user.name || "(no name)"}
          </p>
          <RoleBadge role={user.role} />
          <StatusBadge status={user.subscription_status} />
          {user.plan && <PlanBadge plan={user.plan} />}
        </div>
        <p className="mt-0.5 truncate text-xs font-semibold text-muted-foreground">{user.email}</p>
      </div>

      {/* Stats chips */}
      <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <User size={11} />
          <span>{user.children_count ?? 0} children</span>
          <span className="text-border">·</span>
          <Brain size={11} />
          <span>{user.total_quizzes ?? 0} quizzes</span>
          {(user.word_requests_count ?? 0) > 0 && (
            <>
              <span className="text-border">·</span>
              <BookBookmark size={11} />
              <span>{user.word_requests_count} words</span>
            </>
          )}
        </div>
        <p className="text-[10px] font-bold text-muted-foreground">{formatRelative(user.last_login_at)}</p>
      </div>

      <CaretDown size={13} className="-rotate-90 shrink-0 text-muted-foreground" />
    </button>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const [users,    setUsers]   = useState(null);
  const [search,   setSearch]  = useState("");
  const [filter,   setFilter]  = useState("");
  const [selected, setSelected] = useState(null);
  const searchRef = useRef(null);

  const load = async (q = search, f = filter) => {
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    if (f) params.set("status", f);
    const data = await api(`/admin/users?${params}`).catch(() => []);
    setUsers(data);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(search, filter), 280);
    return () => clearTimeout(t);
  }, [search, filter]); // eslint-disable-line

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-users">
      <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Users</h1>
      <p className="mt-1 text-sm font-semibold text-muted-foreground">
        {users ? `${users.length} user${users.length !== 1 ? "s" : ""}` : "Loading…"}
      </p>

      {/* Search + filter row */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlass
            size={16} weight="bold"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="w-full rounded-2xl border border-border bg-card py-2.5 pl-9 pr-9 text-sm font-semibold text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`rounded-full px-3 py-1.5 text-xs font-extrabold transition ${
                filter === f.value
                  ? "bg-primary text-white shadow-sm"
                  : "bg-muted text-muted-foreground hover:bg-border"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-3xl border border-border bg-card shadow-soft">
        {users === null && (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}
        {users?.length === 0 && (
          <p className="py-12 text-center text-sm font-semibold text-muted-foreground">No users found.</p>
        )}
        {users?.map((u, i) => (
          <div key={u.user_id}>
            {i > 0 && <div className="mx-4 border-t border-border" />}
            <UserRow user={u} onClick={() => setSelected(u)} />
          </div>
        ))}
      </div>

      {/* Detail drawer */}
      {selected && (
        <UserDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onRefresh={() => {
            load();
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}
